import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { formatNumber } from 'accounting';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { isLegManagementEnabled } from 'helpers/legs-helper';
import {
  addAdjustment,
  updateVendorPayable,
} from 'actions/ShipmentsActionCreators';
import { RECEIVABLES } from 'constants/LedgerTypes';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import { showToast } from 'stores/ToastStore';
import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import CarrierCharges from './CarrierCharges';
import ShipperCharges from './ShipperCharges';
import VendorPayables from './VendorPayables';
import AddShorthaulAdjustmentModal from './AddShorthaulAdjustmentModal';
import Summary from './Summary';
import { shipmentTypesForDisplayRateCard } from 'utils/documentUtils';

export default function CargomaticAccounting({
  shipment,
  accessorialRequests,
  onAddCustomAdjustment,
  onAddBaseCost,
  refreshShipment,
  onApproveBaseCost,
  onRemoveLineItem,
  onAddShipperCostAdjustment,
}) {
  const { t } = useTranslation();
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [ledgerToAdjust, setLedgerToAdjust] = useState(RECEIVABLES);
  const [vendorPayableToAdjust, setVendorPayableToAdjust] = useState(null);
  const [itemToAdjust, setItemToAdjust] = useState({});
  const [type, setType] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState();

  function handleHideAdjustmentModel() {
    setShowAdjustmentModal(false);
  }

  function handleAddInternalAdjustment(ledger) {
    return function (item) {
      setShowAdjustmentModal(true);
      setLedgerToAdjust(ledger);
      setItemToAdjust(item);
      setType('internal');
    };
  }

  async function confirmModalAction() {
    try {
      const response = await updateVendorPayable(vendorPayableToAdjust);
      if (response) {
        showToast(t('SUCCESS'), t('UPDATE_VENDOR_PAYABLES_SUCCESS'), 'success');
        setShowConfirmationModal(false);
        refreshShipment();
      } else {
        showToast('', t('UPDATE_VENDOR_PAYABLES_FAILURE'), 'error');
      }
    } catch (_err) {
      showToast('', t('UPDATE_VENDOR_PAYABLES_FAILURE'), 'error');
    }
  }

  function handleAddCustomerAdjustment(ledger) {
    return function (item) {
      setShowAdjustmentModal(true);
      setLedgerToAdjust(ledger);
      setItemToAdjust(item);
      setType('customer');
    };
  }

  function showConfirmation(
    lineItem,
    shipmentId,
    status,
    lineItemMessage,
    cost,
  ) {
    setVendorPayableToAdjust({
      lineItem,
      shipmentId,
      status: status === 'paid' ? 'unpaid' : 'paid',
    });
    setConfirmationMessage(
      t('SET_VENDOR_CHARGE_MESSAGE', {
        status: status === 'paid' ? 'unpaid?' : 'paid?',
        lineItemMessage,
        cost,
      }),
    );
    setShowConfirmationModal(true);
  }

  function handleAddCustomAdjustment(ledger, total) {
    return function (item) {
      let message;
      const { processed, category, type } = item;
      if (!processed && category === 'base_cost') {
        message = item.message;
      } else if (item.message.endsWith('Adjustment')) {
        message = item.message;
      } else if (type === 'fuel_surcharge') {
        message = item.message;
      } else {
        message = t('ITEM_MESSAGE', { itemMessage: item.message });
      }

      let appointmentWindow;
      if (item.appointment) {
        const { market } = shipment;

        const timezone = market.timezone || 'America/Los_Angeles';
        appointmentWindow = {
          begin_time: moment(item.begin_datetime).tz(timezone),
          end_time: moment(item.end_datetime).tz(timezone),
        };
      }

      onAddCustomAdjustment({
        ...item,
        begin_date: item.begin_datetime ? moment(item.begin_datetime) : null,
        end_date: item.end_datetime ? moment(item.end_datetime) : null,
        ...appointmentWindow,
        value: item.parent_adjustment_id ? item.value : -1 * item.value,
        message,
        ledger,
        total,
      });
    };
  }

  function handleAddAdjustment(adjustment) {
    addAdjustment(shipment, ledgerToAdjust, adjustment);
  }

  const {
    _id: shipmentId,
    market: { timezone },
    fsc,
    receivables,
    payables,
    tariff_used,
    vendor_payables,
    distance_in_miles,
    shipment_type,
    current: shipmentIsCurrent,
  } = shipment;

  const totalReceivableRate = parseFloat(getLedgerTotal(receivables));
  const totalPayableRate = parseFloat(getLedgerTotal(payables));
  const totalVendorPayableRate = parseFloat(getLedgerTotal(vendor_payables));
  const intermodalShipmentType = isIntermodalShipmentType(shipment);

  const grossProfit =
    totalReceivableRate - totalPayableRate - totalVendorPayableRate;
  const grossMargin = formatNumber(
    totalReceivableRate <= 0 ? 0 : (grossProfit / totalReceivableRate) * 100,
  );

  const pendingRevenue = getPendingAccessorialTotal(
    accessorialRequests,
    'shipper',
  );

  const pendingCogs =
    getPendingAccessorialTotal(accessorialRequests, 'carrier') +
    getPendingAccessorialTotal(accessorialRequests, 'vendor');

  const fscPercentage = fsc ? (fsc * 100).toFixed(2) : null;

  return (
    <div data-testid="div-2e61e7c5" className="">
      {!isLegManagementEnabled(shipment?.order) && (
        <Summary
          shipperTotal={totalReceivableRate}
          carrierTotal={totalPayableRate}
          vendorTotal={totalVendorPayableRate}
          grossProfit={grossProfit}
          grossMargin={grossMargin}
          shipperFSC={fscPercentage}
          carrierFSC={fscPercentage}
          intermodalShipmentType={intermodalShipmentType}
          tariff={tariff_used}
          currency={shipment?.currency}
          pendingRevenue={pendingRevenue}
          pendingCogs={pendingCogs}
        />
      )}
      <div data-testid="div-076d8a21" className="mb-8">
        <ShipperCharges
          shipmentId={shipmentId}
          timezone={timezone}
          charges={receivables}
          total={totalReceivableRate}
          isIntermodalShipmentType={intermodalShipmentType}
          shipmentIsCurrent={shipmentIsCurrent}
          onAddCustomAdjustment={handleAddCustomAdjustment(
            'receivables',
            totalReceivableRate,
          )}
          onAddInternalAdjustment={handleAddInternalAdjustment('receivables')}
          onAddCustomerAdjustment={handleAddCustomerAdjustment('receivables')}
          onAddBaseCost={onAddBaseCost}
          onRemoveLineItem={onRemoveLineItem}
          onAddShipperCostAdjustment={(lineItem) =>
            onAddShipperCostAdjustment(lineItem)
          }
          currency={shipment?.currency}
          shipperId={shipment?.shipper?._id}
          showDownloadRateCard={shipmentTypesForDisplayRateCard.includes(
            shipment_type,
          )}
          legManagementEnabled={isLegManagementEnabled(shipment?.order)}
        />
      </div>
      <div data-testid="div-0498b474" className="mb-8">
        <CarrierCharges
          shipmentId={shipmentId}
          timezone={timezone}
          total={totalPayableRate}
          charges={payables}
          isIntermodalShipmentType={intermodalShipmentType}
          shipmentIsCurrent={shipmentIsCurrent}
          onAddCustomAdjustment={handleAddCustomAdjustment(
            'payables',
            totalPayableRate,
          )}
          onAddInternalAdjustment={handleAddInternalAdjustment('payables')}
          onAddCustomerAdjustment={handleAddCustomerAdjustment('payables')}
          onAddBaseCost={onAddBaseCost}
          onApproveBaseCost={onApproveBaseCost}
          onRemoveLineItem={onRemoveLineItem}
          currency={shipment?.currency}
          carrierId={shipment?.carrier?._id}
          showDownloadRateCard={shipmentTypesForDisplayRateCard.includes(
            shipment_type,
          )}
        />
      </div>
      {intermodalShipmentType && (
        <div data-testid="div-561501fd" className="mb-8">
          <VendorPayables
            charges={vendor_payables}
            shipmentId={shipmentId}
            shipment={shipment}
            showConfirmation={showConfirmation}
            total={totalVendorPayableRate}
            isLegManagementEnabled={isLegManagementEnabled(shipment?.order)}
          />
        </div>
      )}
      {showAdjustmentModal && (
        <AddShorthaulAdjustmentModal
          show={showAdjustmentModal}
          ledger={ledgerToAdjust}
          item={itemToAdjust}
          items={ledgerToAdjust === 'receivables' ? receivables : payables}
          onAddAdjustment={handleAddAdjustment}
          onHide={handleHideAdjustmentModel}
          mileDistance={distance_in_miles}
          shipmentType={shipment_type}
          type={type}
          currency={shipment?.currency}
        />
      )}
      {showConfirmationModal && (
        <ConfirmModal
          dataTestId="ConfirmModal-c431d66a"
          show={showConfirmationModal}
          title={titleize(t('CONFIRM'))}
          message={confirmationMessage}
          onConfirm={() => confirmModalAction()}
          onHide={() => setShowConfirmationModal(false)}
          affirmative={titleize(t('CONFIRM'))}
          hideButtonText={titleize(t('CANCEL'))}
          primaryButtonColor="green"
        />
      )}
    </div>
  );
}

CargomaticAccounting.propTypes = {
  onAddCustomAdjustment: PropTypes.func.isRequired,
  shipment: PropTypes.object,
  accessorialRequests: PropTypes.array,
};

function getLedgerTotal(ledgerItems = []) {
  return ledgerItems
    .reduce((total, ledgerItem) => {
      return total + parseFloat(ledgerItem.value);
    }, 0)
    .toFixed(2);
}

function getPendingAccessorialTotal(accessorialRequests, userType) {
  let sum = 0;

  // Define the conditions based on the userType
  const conditions = {
    shipper: {
      payerType: 'shipper',
      statusKey: 'shipper_status',
      costKey: 'shipper_cost',
      typeKey: 'payer_type',
    },
    carrier: {
      payerType: 'carrier',
      statusKey: 'carrier_status',
      costKey: 'carrier_cost',
      typeKey: 'payee_type',
    },
    vendor: {
      payerType: 'vendor',
      statusKey: 'carrier_status',
      costKey: 'carrier_cost',
      typeKey: 'payee_type',
    },
  };

  if (conditions[userType] && Array.isArray(accessorialRequests)) {
    const { payerType, statusKey, costKey, typeKey } = conditions[userType];

    accessorialRequests.forEach((acc) => {
      if (
        acc[typeKey] === payerType &&
        !['declined', 'approved'].includes(acc[statusKey])
      ) {
        sum += acc[costKey] || 0;
      }
    });
  }

  return sum;
}
